import type { Distance, Margins, Rectangle } from "@pdfking/math"
import { Floor, Max, RemoveMargins, RotateMarginsLeft, ToSpatial, ToMargins } from "@pdfking/math"
import { ZERO_MARGINS } from "@pdfking/math/constants"
import { NumberQ } from "@pdfking/structs"
import { CreateSingleton, SortBy } from "@pdfking/tfunc"

import type { ORXY, XY } from "#internal"
import {
  BLANK_MATRIX,
  BLANK_NESTED,
  fill_len,
  orxy_bool,
  resolve_fill,
  sym_grow_rotate,
  xy_bool,
} from "#internal"
import { FillFlip } from "#public"
import type { BestFill, FillOptions, FillStrategyData, SymmetryKind } from "#public"

const floor = Floor

/** A fill that stands twice on the sheet keeps the same border at both ends. */
const security_max_symmetry = (security: Margins<Distance>): Margins<Distance> => ({
  top: Max(security.top, security.bottom) as Distance,
  bottom: Max(security.top, security.bottom) as Distance,
  left: Max(security.left, security.right) as Distance,
  right: Max(security.left, security.right) as Distance,
})

const normalize_security = (
  security: Margins<Distance> | undefined,
  use_symmetry: SymmetryKind
): Margins<Distance> => {
  if (!security) return ZERO_MARGINS
  if (use_symmetry) return security_max_symmetry(security)
  return security
}

const has_flip = (s: FillStrategyData): boolean => orxy_bool(s.subjects_flipped)

const x_symmetry = (s: FillStrategyData): boolean => {
  if (fill_len(s) <= 0) return true
  return Boolean(
    s.symmetric_grow.x ||
    (s.subjects.x % 2 === 0 && !xy_bool(s.subjects_flipped.x) && s.subjects_flipped.y.x % 2 === 0)
  )
}

const y_symmetry = (s: FillStrategyData): boolean => {
  if (fill_len(s) <= 0) return true
  return Boolean(
    s.symmetric_grow.y ||
    (s.subjects.y % 2 === 0 && !xy_bool(s.subjects_flipped.y) && s.subjects_flipped.x.y % 2 === 0)
  )
}

const has_symmetry = (s: FillStrategyData): boolean => x_symmetry(s) || y_symmetry(s)

const perfect_symmetry = (s: FillStrategyData): boolean => x_symmetry(s) && y_symmetry(s)

function* inner_fill(opts: {
  canvas: Rectangle<Distance>
  outer: Rectangle<Distance>
  inner: Rectangle<Distance>
  padding: Margins<Distance>
  flip: FillFlip
  symmetric_grow: XY
  security: Margins<Distance>
  use_symmetry: SymmetryKind
}): Generator<FillStrategyData> {
  const inv_symmetry =
    opts.use_symmetry === "x" ? "y" : opts.use_symmetry === "y" ? "x" : opts.use_symmetry

  const w = opts.inner.width + opts.padding.left + opts.padding.right
  const h = opts.inner.height + opts.padding.top + opts.padding.bottom

  const attempts: Array<{
    canvas_width: Distance
    canvas_height: Distance
    outer_width: Distance
    outer_height: Distance
    symmetric_grow: XY
    use_symmetry: SymmetryKind
    security: Margins<Distance>
  }> = [
    {
      canvas_width: opts.canvas.width,
      canvas_height: opts.canvas.height,
      outer_width: opts.outer.width,
      outer_height: opts.outer.height,
      symmetric_grow: opts.symmetric_grow,
      use_symmetry: opts.use_symmetry,
      security: opts.security,
    },
    {
      canvas_width: opts.canvas.height,
      canvas_height: opts.canvas.width,
      outer_width: opts.outer.height,
      outer_height: opts.outer.width,
      symmetric_grow: sym_grow_rotate(opts.symmetric_grow),
      use_symmetry: inv_symmetry,
      // Python fill.py:58 `margins.rotate_fields()` (no reverse) == Left.
      security: RotateMarginsLeft(opts.security),
    },
  ]

  const allowed =
    opts.flip === FillFlip.FLIP_AS_IS
      ? attempts.slice(0, 1)
      : opts.flip === FillFlip.FLIP_ROTATED
        ? attempts.slice(1)
        : attempts
  const strip = opts.flip === FillFlip.FLIP_EFFICIENT

  for (const a of allowed) {
    const subjects: XY = {
      x: floor(a.outer_width / w),
      y: floor(a.outer_height / h),
    }
    if (!subjects.x || !subjects.y) continue

    const flipped_x: XY = strip
      ? {
          x: floor((a.outer_width - subjects.x * w) / h),
          y: floor(a.outer_height / w),
        }
      : BLANK_MATRIX

    const flipped_y: XY = strip
      ? {
          x: floor(a.outer_width / h),
          y: floor((a.outer_height - subjects.y * h) / w),
        }
      : BLANK_MATRIX

    const subjects_flipped: ORXY = strip
      ? {
          x: xy_bool(flipped_x) ? flipped_x : BLANK_MATRIX,
          y: xy_bool(flipped_y) ? flipped_y : BLANK_MATRIX,
        }
      : BLANK_NESTED

    const strategy: FillStrategyData = {
      ...ToSpatial({ width: a.canvas_width, height: a.canvas_height }),
      inner: opts.inner,
      padding: opts.padding,
      security: a.security,
      subjects,
      subjects_flipped,
      symmetric_grow: a.symmetric_grow,
      use_symmetry: a.use_symmetry,
    }

    // The turn axis as seen in this attempt's frame: the turned canvas swaps it
    // (Python rebinds `use_symmetry` per attempt, fill.py:58).
    if (a.use_symmetry) {
      if (a.use_symmetry === "x" || a.use_symmetry === "y") {
        if (
          (a.use_symmetry === "x" && x_symmetry(strategy)) ||
          (a.use_symmetry === "y" && y_symmetry(strategy))
        )
          yield strategy
      } else if (has_symmetry(strategy)) {
        yield strategy
      }
    } else {
      yield strategy
    }
  }
}

/**
 * The best of the candidates: most subjects first, then the one that turns
 * nothing, then the one that stands symmetrically, and at last the one found
 * on the sheet as it stands, so the subjects keep its orientation. The last
 * set holds the empty fill, so there is always an answer — a sheet nothing
 * fits on comes back with no subjects rather than with nothing.
 */
const get_first_fill = (
  canvas: Rectangle<Distance>,
  ...result_sets: Array<Iterable<FillStrategyData>>
): FillStrategyData => {
  const all = result_sets.flatMap(r => [...r])
  const sorted = SortBy(all, s => [
    -fill_len(s),
    has_flip(s) ? 1 : 0,
    perfect_symmetry(s) ? 0 : 1,
    has_symmetry(s) ? 1 : 0,
    s.width === canvas.width ? 0 : 1,
  ])
  return sorted[0] as FillStrategyData
}

const with_layout = (data: FillStrategyData): BestFill => {
  return {
    ...data,
    layout: CreateSingleton(() =>
      resolve_fill({ ...data, flip: true, position: { height: data.height, width: data.width } })
    ),
  }
}

export const GetBestFill = (
  canvas: Rectangle<Distance>,
  inner: Rectangle<Distance>,
  opts: FillOptions = { use_symmetry: undefined }
): BestFill => {
  const use_symmetry = opts.use_symmetry
  const security = normalize_security(opts.security, use_symmetry)
  const padding = NumberQ(opts.padding)
    ? ToMargins(opts.padding as Distance)
    : (opts.padding ?? ZERO_MARGINS)
  // Only the size matters here — the search works in a frame of its own.
  const outer = RemoveMargins(ToSpatial(canvas), security)
  const flip = opts.flip ?? FillFlip.FLIP_EFFICIENT

  const curried = (outer_box: Rectangle<Distance>, symmetric_grow: XY) =>
    inner_fill({
      canvas,
      outer: outer_box,
      inner,
      padding,
      flip,
      symmetric_grow,
      security,
      use_symmetry,
    })

  return with_layout(
    get_first_fill(
      canvas,
      curried(outer, BLANK_MATRIX),
      curried({ width: outer.width, height: (outer.height / 2) as Distance }, { x: 0, y: 1 }),
      curried({ width: (outer.width / 2) as Distance, height: outer.height }, { x: 1, y: 0 }),
      [
        {
          ...ToSpatial(canvas),
          inner,
          padding,
          security,
          subjects: BLANK_MATRIX,
          subjects_flipped: BLANK_NESTED,
          symmetric_grow: BLANK_MATRIX,
          use_symmetry,
        },
      ]
    )
  )
}
